import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { FieldDefinition, HighlightRule, LoanRecord, PanelField, ToneColors } from '../../types/highlights';
import { styleForTone, toneLabels, toneOrder } from '../../utils/palette';
import { describeRule, ruleMatches } from '../../utils/rules';
import { ConditionFields } from './ConditionFields';
interface RulesEditorProps {
  open: boolean;
  focusFieldId: string | null;
  fields: PanelField[];
  catalog: FieldDefinition[];
  byId: Map<string, FieldDefinition>;
  rules: HighlightRule[];
  loan: LoanRecord;
  toneColors: ToneColors;
  onAddRule: (targetFieldId: string) => string;
  onUpdateRule: (ruleId: string, patch: Partial<HighlightRule>) => void;
  onRemoveRule: (ruleId: string) => void;
  onClose: () => void;
}
export function RulesEditor({
  open,
  focusFieldId,
  fields,
  catalog,
  byId,
  rules,
  loan,
  toneColors,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onClose
}: RulesEditorProps) {
  const [scope, setScope] = useState<string>('all');
  useEffect(() => {
    if (open) setScope(focusFieldId ?? 'all');
  }, [open, focusFieldId]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  const panelFieldDefinitions = useMemo(() => fields.map(field => byId.get(field.fieldId)).filter((definition): definition is FieldDefinition => Boolean(definition)), [fields, byId]);
  const visibleRules = useMemo(() => scope === 'all' ? rules : rules.filter(rule => rule.targetFieldId === scope), [rules, scope]);
  if (!open) return null;
  const scopeTargetId = scope === 'all' ? panelFieldDefinitions[0]?.id : scope;
  return createPortal(<div className="fixed inset-0 z-[9998] flex items-start justify-center bg-slate-900/30 p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-149" data-mp="1lccmxibdfpfe">
      <div role="dialog" aria-modal="true" aria-label="Highlight rules" className="mt-10 flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" data-id="element-150" data-mp="1bf7vb2yrb1r7">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3" data-id="element-151" data-mp="1z40vjbmevvrm">
          <div data-id="element-152" data-mp="1udaprxa2pqwe">
            <h2 className="text-sm font-bold text-slate-900" data-id="element-153" data-mp="1ucsjs8xlzyba">Highlight rules</h2>
            <p className="text-[12px] text-slate-500" data-id="element-154" data-mp="1pe7jyam9rgnd">
              Color a field based on its own value or any other loan field. The first matching
              rule wins.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close rules editor" className="rounded p-1 text-slate-400 transition hover:bg-slate-50 hover:text-slate-700" data-id="element-155" data-mp="2i3512aeqv7ej">
            <XIcon className="h-4 w-4" data-id="element-156" data-mp="2ue5rnnkinzh2" />
          </button>
        </header>

        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2" data-id="element-157" data-mp="2zbo2xkm0aqs8">
          <label className="flex items-center gap-2 text-[12px] text-slate-600" data-id="element-158" data-mp="34bk65hmvqe6q">
            Field
            <select value={scope} onChange={event => setScope(event.target.value)} className="rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-159" data-mp="39b6izpyspsf0">
              <option value="all" data-id="element-160" data-mp="3n1s5rprdemi6">All panel fields</option>
              {panelFieldDefinitions.map(definition => <option key={definition.id} value={definition.id} data-id="element-161" data-mp="3s1eily3ae0qg">
                  {definition.label}
                </option>)}
            </select>
          </label>
          <button type="button" disabled={!scopeTargetId} onClick={() => scopeTargetId && onAddRule(scopeTargetId)} className="inline-flex items-center gap-1.5 rounded-md border border-[#cfe0f5] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff] disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-162" data-mp="xlyp439e5g6k">
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-163" data-mp="emul58wpiclr" />
            New rule
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-id="element-164" data-mp="9ot4svqo0rdz">
          {visibleRules.length === 0 ? <p className="py-10 text-center text-[13px] text-slate-500" data-id="element-165" data-mp="nou1dyzwdcnn">
              No rules yet. Add one to color this field automatically.
            </p> : <ul className="space-y-2" data-id="element-166" data-mp="1z2eocjidhwtg">
              {visibleRules.map(rule => {
            const isMatching = rule.enabled && ruleMatches(rule, loan, byId);
            return <li key={rule.id} className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-id="element-167" data-mp="22l57gn4mwxif">
                    <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-600" data-id="element-168" data-mp="27hvkt2p21f05">
                      <span data-id="element-169" data-mp="1sosexsktspp9">Color</span>
                      <select aria-label="Field to color" value={rule.targetFieldId} onChange={event => onUpdateRule(rule.id, {
                  targetFieldId: event.target.value
                })} className="max-w-[170px] rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] font-medium text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-170" data-mp="1xl92olo4521t">
                        {panelFieldDefinitions.map(definition => <option key={definition.id} value={definition.id} data-id="element-171" data-mp="390xmx59zfmyx">
                            {definition.label}
                          </option>)}
                      </select>
                      <span data-id="element-172" data-mp="3dy3360cm049x">when</span>
                      <ConditionFields condition={rule} catalog={catalog} byId={byId} onChange={patch => onUpdateRule(rule.id, patch)} data-id="element-173" data-mp="3dz4ocxnca75d" />
                      {isMatching ? <span className="ml-1 rounded-full bg-[#0b5cab] px-2 py-0.5 text-[10.5px] font-bold text-white" data-id="element-174" data-mp="2zytvjp2gyihk">
                          Matching now
                        </span> : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-175" data-mp="2m6nhq9msgnla">
                      <div role="radiogroup" aria-label="Highlight color" className="flex items-center gap-1" data-id="element-176" data-mp="2m4kh6vo0fc2b">
                        {toneOrder.map(tone => {
                    const active = rule.tone === tone;
                    return <button key={tone} type="button" role="radio" aria-checked={active} aria-label={toneLabels[tone]} title={toneLabels[tone]} onClick={() => onUpdateRule(rule.id, {
                      tone
                    })} className={['h-5 w-5 rounded border-2 transition', styleForTone(tone, toneColors).swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-177" data-mp="woqilzu2z373" />;
                  })}
                      </div>

                      <input aria-label="Badge text" value={rule.flag ?? ''} placeholder="Badge text (optional)" onChange={event => onUpdateRule(rule.id, {
                  flag: event.target.value
                })} className="w-44 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-178" data-mp="2jtwazl5uujlo" />

                      <button type="button" role="switch" aria-checked={rule.enabled} aria-label="Rule enabled" onClick={() => onUpdateRule(rule.id, {
                  enabled: !rule.enabled
                })} className={['relative h-4 w-8 shrink-0 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', rule.enabled ? 'bg-[#0b5cab]' : 'bg-slate-300'].join(' ')} data-id="element-179" data-mp="2xsma16c8itco">
                        <span aria-hidden="true" className={['absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', rule.enabled ? 'left-[18px]' : 'left-0.5'].join(' ')} data-id="element-180" data-mp="8wi45inv3nzz" />
                      </button>

                      <p className="min-w-0 flex-1 truncate text-[11px] text-slate-500" data-id="element-181" data-mp="3m6pcjpxncogy">
                        {describeRule(rule, byId)}
                      </p>

                      <button type="button" onClick={() => onRemoveRule(rule.id)} aria-label="Delete rule" className="rounded p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-700" data-id="element-182" data-mp="3cgmlhva35kl9">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-183" data-mp="1abge8qfa1pur" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-2.5" data-id="element-184" data-mp="10fwn0hbnrfcc">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-50" data-id="element-185" data-mp="15deyaed5e6ni">
            Done
          </button>
        </footer>
      </div>
    </div>, document.body);
}